/**
 * Landing v2 — "Upptäck områden" carousel.
 * Horizontal swipe on mobile, 3 cards on desktop.
 * Reuses media_assets SoT via useLandingAreas.
 */
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLandingAreas } from '@/hooks/useLandingAreas';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslate } from '@/i18n';

export default function AreasCarouselSection() {
  const { data: areas = [], isLoading } = useLandingAreas();
  const t = useTranslate();

  if (isLoading) return <CarouselSkeleton />;
  if (areas.length === 0) return null;

  return (
    <section className="py-16 sm:py-24">
      <div className="container max-w-6xl px-5">
        {/* Header */}
        <p className="text-xs tracking-widest uppercase text-muted-foreground/60 mb-2">
          {t("home.areas.kicker")}
        </p>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-foreground tracking-[-0.015em] leading-[1.15] mb-3">
          {t("home.areas.title")}
        </h2>
        <p className="text-base text-muted-foreground leading-relaxed mb-8 sm:mb-10 max-w-lg">
          {t("home.areas.sub")}
        </p>

        {/* Scroll container */}
        <div className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-5 px-5 scrollbar-none">
          {areas.map((area) => (
            <AreaCard key={area.id} area={area} />
          ))}
        </div>
      </div>
    </section>
  );
}

function AreaCard({ area }: { area: ReturnType<typeof useLandingAreas>['data'] extends (infer T)[] ? T : never }) {
  const t = useTranslate();
  return (
    <Link
      to={area.href}
      className="group flex-shrink-0 w-[75vw] sm:w-[45%] lg:w-[31%] snap-start rounded-xl overflow-hidden bg-background shadow-sm hover:shadow-md transition-shadow duration-300"
    >
      {/* Image */}
      <div className="aspect-[3/2] overflow-hidden bg-muted">
        {area.coverUrl ? (
          <img
            src={area.coverUrl}
            alt={area.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-muted to-muted/60 flex items-center justify-center">
            <span className="text-muted-foreground/40 font-display text-lg">
              {area.name}
            </span>
          </div>
        )}
      </div>

      {/* Text */}
      <div className="p-4 sm:p-5 flex flex-col gap-2">
        <h3 className="font-display text-lg font-semibold text-foreground leading-tight">
          {area.name}
        </h3>

        {area.descriptionShort && (
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
            {area.descriptionShort}
          </p>
        )}

        <span className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-accent group-hover:text-accent/80 transition-colors w-fit">
          {t("cta.explore")}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}

function CarouselSkeleton() {
  return (
    <section className="py-16 sm:py-24">
      <div className="container max-w-6xl px-5">
        <Skeleton className="h-3 w-28 mb-2" />
        <Skeleton className="h-8 w-64 mb-3" />
        <Skeleton className="h-5 w-80 mb-8" />
        <div className="flex gap-5 overflow-hidden">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex-shrink-0 w-[75vw] sm:w-[45%] lg:w-[31%] space-y-3">
              <Skeleton className="aspect-[3/2] rounded-xl w-full" />
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
